// =============================================================================
// lib/storageDownload.ts
//
// Force a Supabase Storage object to download with a specific filename.
// Browsers default to the storage path's leaf segment (a UUID + timestamp)
// which is meaningless — riggers/managers want files named like
// "Working at Heights - Allan McGowen - Exp 20270718_FRONT.jpg".
//
// Mechanism: Supabase's createSignedUrl accepts a `download` option (string
// = filename, true = use the path's basename). Setting it adds a
// Content-Disposition: attachment; filename="..." header to the signed
// response. We then trigger the download via a temporary anchor tag.
//
// Same pattern works for any Supabase Storage bucket — pass a friendly
// filename when calling.
// =============================================================================

import { supabase } from "./supabase";

/**
 * Trigger a forced download of a Supabase Storage object with a
 * human-friendly filename. Returns the signed URL used (handy for tests).
 */
export async function downloadFromStorage(
  bucket: string,
  storagePath: string,
  filename: string,
  ttlSeconds: number = 60,
): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(storagePath, ttlSeconds, { download: filename });

  if (error || !data?.signedUrl) {
    console.error("[storageDownload] failed", { bucket, storagePath, error });
    throw new Error(error?.message ?? "Couldn't generate download link");
  }

  // Trigger via temporary anchor — works on all modern browsers, doesn't
  // require user interaction beyond the click that called this.
  const a = document.createElement("a");
  a.href = data.signedUrl;
  a.download = filename;       // belt+braces: also signals download intent
  a.target = "_blank";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();

  return data.signedUrl;
}

/**
 * Strip characters that won't survive intact across Windows/macOS/Linux
 * filesystems (and Aussie naming conventions).
 */
export function safeFilenamePart(s: string | null | undefined): string {
  if (!s) return "";
  return s
    .replace(/[\\/:*?"<>|]/g, "")   // forbidden on Windows
    .replace(/\s+/g, " ")           // collapse whitespace
    .trim();
}
